import React, { useState } from "react";
import "./Overview.css";
import cx from "classnames";
import ArrowIcon from "img/Arrow.svg";
import ColleteralIcon from "img/Colleteral.svg";
import TotalTradesIcon from "img/TotalTrades.svg";
import TotalVolumeIcon from "img/TotalVolume.svg";
import PalmToken from "img/PalmToken.svg";
import PlpToken from "img/plpToken.png";
import tetherIcon from "img/tetherIcon.png";
import PositionsListPortfolio from "../PositionListPortfolio/PositionListPortfolio";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "lib/chains";
import { contractFetcher } from "lib/contracts";
import useSWR from "swr";
import Reader from "abis/Reader.json";
import { getContract } from "config/contracts";
import { getPositionQuery } from "../../Exchange/getPositionQuery";
import { getTokens, getWhitelistedTokens } from "config/tokens";
import { useLocalStorageByChainId } from "lib/localStorage";
import { useInfoTokens } from "domain/tokens";
import { getPositions } from "pages/Exchange/getPositions";
import { formatAmount } from "lib/numbers";
import { USD_DECIMALS } from "lib/legacy";
import { BigNumber } from "ethers";
import { usePlpBalance } from "pages/Event/hooks/blockchain/usePlpBalance";

import { useTradeHistory } from "components/Exchange/hooks/useTradeHistory";

const Overview = ({ savedIsPnlInLeverage, savedShowPnlAfterFees, connectWallet, setActivePage, setActiveFilter }) => {
  const [updatedPositions] = useState({});
  const [pendingPositions, setPendingPositions] = useState({});
  const [isWaitingForPluginApproval, setIsWaitingForPluginApproval] = useState(false);
  const [isWaitingForPositionRouterApproval, setIsWaitingForPositionRouterApproval] = useState(false);
  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const POSITIONS = "Positions";
  const ORDERS = "Orders";
  const TRADES = "Trades";

  const flagOrdersEnabled = true;

  const { active, account, library } = useWeb3React();
  const { chainId } = useChainId();

  const readerAddress = getContract(chainId, "Reader");
  const nativeTokenAddress = getContract(chainId, "NATIVE_TOKEN");
  const vaultAddress = getContract(chainId, "Vault");
  const whitelistedTokens = getWhitelistedTokens(chainId);
  const stakedPLPAmount = usePlpBalance();

  const positionQuery = getPositionQuery(whitelistedTokens, nativeTokenAddress);

  const { data: positionData, error: positionDataError } = useSWR(
    active && [active, chainId, readerAddress, "getPositions", vaultAddress, account],
    contractFetcher(library, Reader, [positionQuery.indexTokens, positionQuery.isLong])
  );

  const tokens = getTokens(chainId);

  const tokenAddresses = tokens.map((token) => token.address);

  const { data: tokenBalances } = useSWR(
    active && [active, chainId, readerAddress, "getTokenBalances", account],
    contractFetcher(library, Reader, [tokenAddresses])
  );

  const whitelistedTokenAddresses = whitelistedTokens.map((token) => token.address);

  const { data: fundingRateInfo } = useSWR(
    [active, chainId, readerAddress, "getFundingRates"],
    contractFetcher(library, Reader, [vaultAddress, nativeTokenAddress, whitelistedTokenAddresses])
  );

  const positionsDataIsLoading = active && !positionData && !positionDataError;

  const LIST_SECTIONS = [POSITIONS, flagOrdersEnabled && ORDERS, TRADES].filter(Boolean);

  let [, setListSection] = useLocalStorageByChainId(chainId, "List-section-v2", LIST_SECTIONS[0]);

  const { infoTokens } = useInfoTokens(library, chainId, active, tokenBalances, fundingRateInfo);

  const { positions, positionsMap } = getPositions(
    chainId,
    positionQuery,
    positionData,
    infoTokens,
    savedIsPnlInLeverage,
    savedShowPnlAfterFees,
    account,
    pendingPositions,
    updatedPositions
  );

  const longPositions = positions.filter((position) => position.isLong);
  const shortPositions = positions.filter((position) => !position.isLong);

  const setFilter = (value) => {
    setActiveFilter(value);
    setActivePage("position");
  };

  // This value will be needed when calculating totalPercentageChange

  // const totalNetValue = positions
  //   .map((position) => BigNumber.from(position.netValue))
  //   .reduce((accumulator, size) => accumulator.add(size), BigNumber.from(0));

  // const formatTotalValue = formatAmount(totalNetValue, USD_DECIMALS, 2, true);

  const totalPnL = positions
    .map((position) => {
      // eslint-disable-next-line
      const sanitizedDeltaStr = position.deltaStr.replace(/[\$+,]/g, "");
      const delta = parseFloat(sanitizedDeltaStr) * (position.isLong ? 1 : -1);
      return isNaN(delta) ? 0 : delta;
    })
    .reduce((accumulator, delta) => accumulator + delta, 0);

  //  const totalPercentageChange is currently incorrect need to fix

  const totalPercentageChange =
    (totalPnL /
      positions.reduce(
        (sum, position) => sum + (parseFloat(position.deltaPercentageStr) / 100) * parseFloat(position.netValue),
        0
      )) *
    100;

  const totalSizeLong = longPositions
    .map((longPosition) => BigNumber.from(longPosition.size))
    .reduce((accumulator, size) => accumulator.add(size), BigNumber.from(0));

  const totalSizeShort = shortPositions
    .map((shortPosition) => BigNumber.from(shortPosition.size))
    .reduce((accumulator, size) => accumulator.add(size), BigNumber.from(0));

  const totalTrades = useTradeHistory(account);

  const totalVolume = totalTrades
    .map((trade) => BigNumber.from(trade.size))
    .reduce((accumulator, size) => accumulator.add(size), BigNumber.from(0));

  const colleteralUsage = positions
    .map((position) => BigNumber.from(position.size))
    .reduce((accumulator, size) => accumulator.add(size), BigNumber.from(0));

  return (
    <div className="Overview default-container">
      <div className="HeaderWrapper">
        <span className="MainHeader">Overview</span>
        <span className="Subheader muted">{currentDate}</span>
      </div>

      <div className="OverviewChartWrapper">
        <div className="OverviewChart"></div>
        <div className="ChartInformations">
          <div className="chartInfo">
            <span className="muted">Current PnL</span>
            <span
              className={cx("OverviewMaintext ", {
                positive: totalPnL > 0,
                negative: totalPnL < 0,
                muted: totalPnL === 0,
              })}
            >
              {totalPnL > 0 ? `+${totalPnL.toFixed(2)} USDT` : `${totalPnL.toFixed(2)} USDT`}
            </span>
            <div className="ChartInfoPercentageWrapper profit">
              <img className="profitArrow" src={ArrowIcon} alt="" />
              <span>
                {totalPercentageChange >= 0
                  ? `+${totalPercentageChange.toFixed(2)}%`
                  : `${totalPercentageChange.toFixed(2)}`}
              </span>
            </div>
            <div className="line"></div>
          </div>
          <div className="chartInfo">
            <span className="muted">Total Open Longs</span>
            <span className="OverviewMaintext"> ${formatAmount(totalSizeLong, USD_DECIMALS, 2, true)}</span>
            <div className="ChartInfoPercentageWrapper">
              <span className="ChartInfoPercentage" onClick={() => setFilter("long")}>
                {longPositions.length} Positions (View)
              </span>
            </div>
          </div>
          <div className="line"></div>
          <div className="chartInfo">
            <span className="muted">Total Open Shorts</span>
            <span className="OverviewMaintext">${formatAmount(totalSizeShort, USD_DECIMALS, 2, true)}</span>
            <div className="ChartInfoPercentageWrapper">
              <span className="ChartInfoPercentage" onClick={() => setFilter("short")}>
                {shortPositions.length} Positions (View)
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="OverviewInfoboxes">
        <div className="Infobox">
          <div className="Infoboxtext">
            <span className="muted">Colletral Usage</span>
            <span className="OverviewMaintext">{formatAmount(colleteralUsage, USD_DECIMALS, 2, true)} USDT</span>
          </div>
          <div className="IconWrapper">
            <img className="InfoboxIcon" src={ColleteralIcon} alt="" />
          </div>
        </div>
        <div className="Infobox">
          <div className="Infoboxtext">
            <span className="muted">My Total Trades</span>
            <span className="OverviewMaintext">{totalTrades.length}</span>
          </div>
          <div className="IconWrapper">
            <img className="InfoboxIcon" src={TotalTradesIcon} alt="" />
          </div>
        </div>
        <div className="Infobox">
          <div className="Infoboxtext">
            <span className="muted">My Total Volume</span>
            <span className="OverviewMaintext">{formatAmount(totalVolume, USD_DECIMALS, 2, true)} USDT</span>
          </div>
          <div className="IconWrapper">
            <img className="InfoboxIcon" src={TotalVolumeIcon} alt="" />
          </div>
        </div>
      </div>
      <div className="OverviewEarnings">
        <div className="OverviewHeaderWrapper">
          <span className="MainHeader">Earnings</span>
        </div>

        <div className="OverviewInfoboxes">
          <div className="Infobox">
            <div className="Infoboxtext">
              <span className="muted">Staked PLP</span>
              <span className="OverviewMaintext">{parseFloat(stakedPLPAmount.formatted).toFixed(2)} PLP</span>
              <span className="muted">Rewards 10,000 USDT</span>
            </div>
            <div className="IconWrapperBottom">
              <img className="InfoboxIconBottom" src={PlpToken} alt="" />
            </div>
          </div>
          <div className="Infobox">
            <div className="Infoboxtext">
              <span className="muted">Staked PALM</span>
              <span className="OverviewMaintext">9.788.14 PALM</span>
              <span className="muted">Rewards 10,000 USDT</span>
            </div>
            <div className="IconWrapperBottom">
              <img className="InfoboxIconBottom" src={PalmToken} alt="" />
            </div>
          </div>
          <div className="Infobox">
            <div className="Infoboxtext">
              <span className="muted">Total Earnings</span>
              <span className="OverviewMaintext">9.788.14 USDT</span>
              <span className="muted">Rewards 10,000 USDT</span>
            </div>
            <div className="IconWrapperBottom">
              <img className="InfoboxIconBottom" src={tetherIcon} alt="" />
            </div>
          </div>
        </div>
      </div>

      <div className="OverviewPosition">
        <div className="OverviewHeaderWrapper">
          <div className="MainHeaderWrapper">
            <span className="MainHeader">Open Positions</span>

            {positions.length > 0 && (
              <div className="openAmount muted">
                <span>{positions.length}</span>
              </div>
            )}
          </div>

          <span className="Subheader muted">View your open positions</span>
        </div>
        <PositionsListPortfolio
          positionsDataIsLoading={positionsDataIsLoading}
          pendingPositions={pendingPositions}
          setPendingPositions={setPendingPositions}
          setListSection={setListSection}
          setIsWaitingForPluginApproval={setIsWaitingForPluginApproval}
          setIsWaitingForPositionRouterApproval={setIsWaitingForPositionRouterApproval}
          isWaitingForPluginApproval={isWaitingForPluginApproval}
          isWaitingForPositionRouterApproval={isWaitingForPositionRouterApproval}
          positions={positions}
          positionsMap={positionsMap}
          infoTokens={infoTokens}
          active={active}
          account={account}
          library={library}
          flagOrdersEnabled={flagOrdersEnabled}
          savedIsPnlInLeverage={savedIsPnlInLeverage}
          chainId={chainId}
          nativeTokenAddress={nativeTokenAddress}
          showPnlAfterFees={savedShowPnlAfterFees}
          connectWallet={connectWallet}
        />
      </div>
    </div>
  );
};

export default Overview;
